/**
 * Options Tape presets: named bundles of filters, sort, view and live flag.
 *
 * The curated set encodes what the retired Top Premium Board (Smart Flow) did by default —
 * contracts ranked by summed premium over the last session, refreshed every 30 s — plus its
 * verdict readings (bought ≥55% of premium at the ask, sold ≤45%, multi-leg ≥60% of labelled
 * volume, from utils/flowVerdictLabels) and the desk views people asked for. Every filter in a
 * curated preset is a server parameter; only the verdict presets read the top contracts the
 * server returned, and the UI says so.
 *
 * Users save their own presets to localStorage (`mmecOptionsTapePresets`).
 */

import { formatPremium } from '../../utils/flowTapeApi';
import type { FlowTapeQuery } from '../../utils/flowTapeApi';
import type { TimeWindowId } from '../../utils/timeWindows';
import type { CustomFilter } from '../../utils/rtlScannerFlowRows';

export type TapeView = 'prints' | 'contracts';
export type VerdictFilter = '' | 'Bought' | 'Sold' | 'Multi-leg';

/** Filter fields a preset can set; time bounds travel separately. */
export type TapeFilterFields = Omit<FlowTapeQuery, 'fromTs' | 'toTs' | 'sortBy' | 'sortDir' | 'limit' | 'offset'>;

export interface TapeSettings {
  filters: TapeFilterFields;
  /** A named session window, '' when explicit bounds are used. */
  window: TimeWindowId | '';
  /** Rolling look-back in minutes; wins over `window` and is re-resolved on every refresh. */
  rollingMinutes: number | null;
  /** Explicit bounds, kept only when neither window nor rolling is set. */
  fromTs: string;
  toTs: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  live: boolean;
  view: TapeView;
  rules: Omit<CustomFilter, 'id'>[];
  verdict: VerdictFilter;
}

export interface TapePreset {
  id: string;
  name: string;
  description: string;
  settings: TapeSettings;
}

export const FILTER_KEYS: (keyof TapeFilterFields)[] = [
  'ticker', 'search', 'side', 'optionType', 'minPremium', 'maxPremium', 'minSize', 'minStrike',
  'maxStrike', 'minDte', 'maxDte', 'expiry', 'moneyness', 'underlyingClass', 'capTier',
];

export const DEFAULT_SETTINGS: TapeSettings = {
  filters: {},
  window: 'last_session',
  rollingMinutes: null,
  fromTs: '',
  toTs: '',
  sortBy: 'premium',
  sortDir: 'desc',
  live: false,
  view: 'prints',
  rules: [],
  verdict: '',
};

function preset(id: string, name: string, description: string, patch: Partial<TapeSettings>): TapePreset {
  return { id, name, description, settings: { ...DEFAULT_SETTINGS, ...patch, filters: { ...(patch.filters ?? {}) } } };
}

export const CURATED_PRESETS: TapePreset[] = [
  preset('all-prints', 'All prints',
    'Every print in the last session, largest premium first. The starting view.',
    {}),
  preset('top-premium-board', 'Top Premium Board',
    'The old Smart Flow default: top 150 contracts by premium summed over the last session, live every 30 s.',
    { view: 'contracts', live: true }),
  preset('top-premium-now', 'Top premium now',
    'Largest prints of the last 30 minutes, newest window re-read every 30 s. Empty while the market is closed.',
    { rollingMinutes: 30, window: '', live: true }),
  preset('whale-prints', 'Whale prints',
    'Single prints of $1M premium or more in the last session, largest first.',
    { filters: { minPremium: '1000000' } }),
  preset('dte-0-5', '0–5 DTE',
    'Prints expiring within five days (0DTE included), largest premium first.',
    { filters: { minDte: '0', maxDte: '5' } }),
  preset('weekly-calls-ask', 'Weekly calls bought at ask',
    'Calls that printed at the ask with 7 days or less to expiry.',
    { filters: { optionType: 'C', side: 'Ask', maxDte: '7' } }),
  preset('puts-ask', 'Puts bought at ask',
    'Puts that printed at the ask, any expiry, largest premium first.',
    { filters: { optionType: 'P', side: 'Ask' } }),
  preset('bought-contracts', 'Bought contracts',
    'Top contracts where at least 55% of premium printed at the ask (Bought verdict). Read from the top 150 by premium.',
    { view: 'contracts', verdict: 'Bought' }),
  preset('sold-contracts', 'Sold contracts',
    'Top contracts where 45% or less of premium printed at the ask (Sold verdict). Read from the top 150 by premium.',
    { view: 'contracts', verdict: 'Sold' }),
  preset('multi-leg', 'Multi-leg structures',
    'Top contracts where 60% or more of labelled volume printed as a complex order. Read from the top 150 by premium.',
    { view: 'contracts', verdict: 'Multi-leg' }),
  preset('index-only', 'Index only',
    'SPX and SPXW prints only. The server takes one class at a time, so ETFs have their own preset.',
    { filters: { underlyingClass: 'index' } }),
  preset('etf-only', 'ETFs only',
    'Prints on ETF underlyings (SPY, QQQ, IWM, sector funds and the rest of the ETF list).',
    { filters: { underlyingClass: 'etf' } }),
  preset('small-caps', 'Small caps',
    'Prints on small-cap stocks, by cached market cap.',
    { filters: { capTier: 'small' } }),
];

// ── comparison ───────────────────────────────────────────────────────────────

/** Canonical form, so "is the active preset still what is on screen" is a string compare. */
export function settingsKey(s: TapeSettings): string {
  const filters: Record<string, string> = {};
  for (const k of FILTER_KEYS) {
    const v = String(s.filters[k] ?? '').trim();
    if (v) filters[k] = v;
  }
  const timed = s.rollingMinutes ? { r: s.rollingMinutes } : s.window ? { w: s.window } : { f: s.fromTs, t: s.toTs };
  return JSON.stringify({
    filters,
    timed,
    sb: s.sortBy,
    sd: s.sortDir,
    live: s.live,
    view: s.view,
    rules: s.rules.filter((r) => r.value.trim()).map((r) => [r.field, r.op, r.value.trim()]),
    verdict: s.view === 'contracts' ? s.verdict : '',
  });
}

// ── user presets ─────────────────────────────────────────────────────────────

export const USER_PRESETS_KEY = 'mmecOptionsTapePresets';
const EXPORT_KIND = 'mmec-options-tape-presets';

function isSettings(v: unknown): v is TapeSettings {
  const s = v as TapeSettings;
  return !!s && typeof s === 'object' && typeof s.filters === 'object' && typeof s.sortBy === 'string';
}

function sanitize(list: unknown): TapePreset[] {
  if (!Array.isArray(list)) return [];
  const out: TapePreset[] = [];
  for (const p of list) {
    if (!p || typeof p !== 'object') continue;
    const { id, name, description, settings } = p as TapePreset;
    if (typeof id !== 'string' || typeof name !== 'string' || !isSettings(settings)) continue;
    out.push({
      id,
      name: name.slice(0, 60),
      description: typeof description === 'string' ? description.slice(0, 200) : '',
      settings: {
        ...DEFAULT_SETTINGS,
        ...settings,
        filters: { ...settings.filters },
        rules: Array.isArray(settings.rules) ? settings.rules : [],
      },
    });
  }
  return out;
}

export function loadUserPresets(): TapePreset[] {
  try {
    return sanitize(JSON.parse(window.localStorage.getItem(USER_PRESETS_KEY) || '[]'));
  } catch {
    return [];
  }
}

export function saveUserPresets(list: TapePreset[]): void {
  try {
    window.localStorage.setItem(USER_PRESETS_KEY, JSON.stringify(list));
  } catch {
    // Storage unavailable: presets last for this visit only.
  }
}

export function newPresetId(): string {
  return `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function exportPresetsJson(list: TapePreset[]): string {
  return JSON.stringify({ kind: EXPORT_KIND, version: 1, presets: list }, null, 2);
}

/** Accepts this module's export, or a bare array of presets. Ids are re-issued to avoid clashes. */
export function parseImportedPresets(text: string): TapePreset[] {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return [];
  }
  const list = Array.isArray(data) ? data : (data as { presets?: unknown })?.presets;
  return sanitize(list).map((p) => ({ ...p, id: newPresetId() }));
}

/** One line for a user preset, built from what it sets. */
export function describeSettings(s: TapeSettings): string {
  const bits: string[] = [];
  const f = s.filters;
  if (f.ticker) bits.push(f.ticker);
  else if (f.search) bits.push(`ticker ~${f.search}`);
  if (f.optionType) bits.push(f.optionType === 'P' ? 'puts' : 'calls');
  if (f.side) bits.push(`at ${f.side.toLowerCase()}`);
  if (f.minPremium) bits.push(`premium ≥ ${formatPremium(Number(f.minPremium))}`);
  if (f.maxPremium) bits.push(`premium ≤ ${formatPremium(Number(f.maxPremium))}`);
  if (f.minDte || f.maxDte) bits.push(`DTE ${f.minDte || '0'}–${f.maxDte || '∞'}`);
  if (f.underlyingClass) bits.push(f.underlyingClass);
  if (f.capTier) bits.push(`${f.capTier} cap`);
  if (f.moneyness) bits.push(f.moneyness);
  if (s.rules.length) bits.push(`${s.rules.length} rule${s.rules.length > 1 ? 's' : ''}`);
  if (s.view === 'contracts') bits.push('contracts');
  if (s.verdict && s.view === 'contracts') bits.push(s.verdict.toLowerCase());
  if (s.rollingMinutes) bits.push(`last ${s.rollingMinutes} min`);
  if (s.live) bits.push('live');
  return bits.join(' · ') || 'All prints';
}

// ── favourites (shared with the scanner boards) ──────────────────────────────

/** Same key the Top Premium Board used, so contracts starred there are still starred here. */
export const FAV_KEY = 'rtlScannerFavorites';

export function loadFavourites(): Set<string> {
  try {
    const v = JSON.parse(window.localStorage.getItem(FAV_KEY) || '[]');
    return new Set(Array.isArray(v) ? v.map(String) : []);
  } catch {
    return new Set();
  }
}

/** Re-reads before writing so stars set on another board since this mount are kept. */
export function toggleFavourite(id: string): Set<string> {
  const next = loadFavourites();
  if (next.has(id)) next.delete(id);
  else next.add(id);
  try {
    window.localStorage.setItem(FAV_KEY, JSON.stringify(Array.from(next)));
  } catch {
    // Storage unavailable.
  }
  return next;
}

/** OCC symbol when the print carries one, else the contract parts. */
export function contractKeyOf(raw: Record<string, unknown> | undefined, parts: { ticker: string; strike: number | null; expiry: string; optionType: string }): string {
  const occ = String(raw?.Contract ?? '').trim();
  if (occ) return occ;
  return `${parts.ticker}|${parts.strike ?? ''}|${parts.expiry}|${parts.optionType}`;
}
